import { authClient } from "../api/client";

export type CompetitionRole =
  | "competition_admin"
  | "judge1"
  | "judge2"
  | "judge3"
  | null;

export const COMPETITION_CURRENT_EVENT_STORAGE_KEY =
  "cover_dance_current_event_id";

const COMPETITION_ROLES: CompetitionRole[] = [
  "competition_admin",
  "judge1",
  "judge2",
  "judge3",
];

export function readStoredCompetitionEventId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(COMPETITION_CURRENT_EVENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function persistJudgeCompetitionEventId(eventId: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COMPETITION_CURRENT_EVENT_STORAGE_KEY, eventId);
  } catch {
    /* ignore */
  }
}

export function clearStoredCompetitionEventId() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(COMPETITION_CURRENT_EVENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function useCompetitionAuth() {
  const { data, isPending } = authClient.useSession();
  const user = data?.user ?? null;
  const role = (user?.role ?? null) as string | null;

  const storedEventId = readStoredCompetitionEventId();

  const isCompetitionRole = (
    r: string | null,
  ): r is NonNullable<CompetitionRole> =>
    COMPETITION_ROLES.includes(r as CompetitionRole);

  const isCompetitionAdmin = role === "competition_admin";
  const isJudge1 = role === "judge1";
  const isJudge2 = role === "judge2";
  const isJudge3 = role === "judge3";
  const isJudge = isJudge1 || isJudge2 || isJudge3;
  const isCompetitionUser = isCompetitionRole(role);

  const judgeNumber = isJudge1 ? 1 : isJudge2 ? 2 : isJudge3 ? 3 : null;

  const currentEventId: string | null = storedEventId;

  const getRedirectPath = (): string => {
    if (isCompetitionAdmin) return "/competition/admin";
    if (
      judgeNumber != null &&
      storedEventId &&
      (isJudge1 || isJudge2 || isJudge3)
    ) {
      return `/competition/events/${storedEventId}/judge/${judgeNumber}`;
    }
    return "/competition/login";
  };

  const signOut = async () => {
    clearStoredCompetitionEventId();
    await authClient.signOut();
  };

  return {
    user,
    role: role as CompetitionRole,
    currentEventId,
    isLoading: isPending,
    isAuthenticated: Boolean(user) && !isPending,
    isCompetitionUser,
    isCompetitionAdmin,
    isJudge,
    isJudge1,
    isJudge2,
    isJudge3,
    judgeNumber,
    getRedirectPath,
    signOut,
  };
}

export type UseCompetitionAuthReturn = ReturnType<typeof useCompetitionAuth>;
